import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Spacer,
  StyleProps,
} from "@chakra-ui/react";
import { Row } from "@tanstack/react-table";
import React from "react";
import { rowExpanderColumnDef } from "src/components/Table/rowExpanderColumnDef";
import { ColumnDefWithTooltip, Table } from "src/components/Table/Table";
import {
  CapacityConfig,
  CapacityImportResult,
  ImportedGradeCapacityRow,
} from "src/generated/hooks/form-service/requests";

type ImportResult = CapacityImportResult[];
type Props = {
  config: CapacityConfig;
  importResult: ImportResult;
};
export const ImportSummary: React.FC<Props> = ({ config, importResult }) => {
  return (
    <Accordion
      allowMultiple
      display="flex"
      flexDirection="column"
      gap="4"
      defaultIndex={importResult.length > 1 ? [] : [0]}
    >
      {importResult.map((row) => {
        return (
          <AccordionItem key={row.schoolId} border="none">
            <Heading as="h2">
              <AccordionButton
                roundedTop="md"
                padding="5"
                border="1px solid"
                borderColor="gray.200"
              >
                <Box fontWeight="bold" color="primary.500">
                  {row.schoolName}
                </Box>
                <Spacer />
                <AccordionIcon />
              </AccordionButton>
            </Heading>
            <AccordionPanel padding="0">
              <ImportSummaryTable config={config} importResult={row} />
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

type TableProps = {
  config: CapacityConfig;
  importResult: CapacityImportResult;
};
const ImportSummaryTable: React.FC<TableProps> = ({ config, importResult }) => {
  const columns = React.useMemo(() => {
    const cols: ColumnDefWithTooltip<ImportedGradeCapacityRow>[] = [
      rowExpanderColumnDef({
        header: "Grade",
        size: 500,
        cell: ({ row }) => {
          return (
            <Flex fontStyle={row.depth > 0 ? "italic" : undefined}>
              {row.original.name}
            </Flex>
          );
        },
      }),
      {
        header: "Total capacity",
        cell: ({ row }) => {
          return <DeltaCell row={row} field="totalCapacity" />;
        },
      },
    ];

    if (config.additionalSeatsTaken1.enabled) {
      cols.push({
        header: config.additionalSeatsTaken1.label,
        cell: ({ row }) => {
          return <DeltaCell row={row} field="additionalSeatsTaken1" />;
        },
      });
    }
    if (config.additionalSeatsTaken2.enabled) {
      cols.push({
        header: config.additionalSeatsTaken2.label,
        cell: ({ row }) => {
          return <DeltaCell row={row} field="additionalSeatsTaken2" />;
        },
      });
    }

    return cols;
  }, [config]);
  return (
    <Table
      expandAllProps={{
        expandAllSubRows: true,
        hideExpandAll: true,
      }}
      tableContainerProps={{ rounded: "none", padding: 0 }}
      hideItemCount
      data={importResult.grades}
      columns={columns}
      getSubRows={(row) => row.programs}
      getCellStyleProps={(cell) => {
        const header = cell.column.columnDef.header;
        const style: StyleProps = {};
        if (
          header === "Total capacity" ||
          header === config.additionalSeatsTaken1.label
        ) {
          style.borderLeft = "1px solid";
          style.borderColor = "gray.200";
        }

        return style;
      }}
      getHeaderStyleProps={(cell) => {
        const header = cell.column.columnDef.header;
        if (
          header === "Total capacity" ||
          header === config.additionalSeatsTaken1.label
        ) {
          return {
            borderLeft: "1px solid",
            borderColor: "gray.200",
            textAlign: "center",
          };
        }

        if (header === "Grade") {
          return {
            paddingLeft: "6",
          };
        }
        return {
          textAlign: "center",
        };
      }}
    />
  );
};

type DeltaCellProps = {
  row: Row<ImportedGradeCapacityRow>;
  field: "totalCapacity" | "additionalSeatsTaken1" | "additionalSeatsTaken2";
};
const DeltaCell = ({ row, field }: DeltaCellProps) => {
  if (
    row.original.changes === undefined ||
    row.original.changes.before === undefined
  ) {
    return null;
  }

  const delta =
    row.original.changes.after[field] - row.original.changes.before[field];
  return (
    <Flex
      justifyContent="center"
      fontStyle={row.depth > 0 ? "italic" : undefined}
    >
      {row.original.changes.after[field]}
      {delta === 0 ? null : null}
      {delta > 0 ? ` (+${delta})` : null}
      {delta < 0 ? ` (${delta})` : null}
    </Flex>
  );
};
